import { useRef, useState } from 'react';

import { Icon } from '../components/Icon.js';
import { KbdKeys } from '../components/Kbd.js';
import { useDismiss } from '../util/useDismiss.js';
import { ListBadge, ListGlyph, listTone } from './ListBadge.js';
import {
  LIST_COLORS,
  LIST_ICONS,
  type ConversationList,
  type ListColor,
  type ListIcon,
  type ListStyle,
} from './useLists.js';
import styles from './ListEditor.module.css';

/**
 * The swatches and tiles, with "no choice" first in both rather than left to a
 * press-the-chosen-one-again gesture nobody would find. The default dress is a
 * real option an employer can come back to, so it looks like one.
 */
const COLOR_OPTIONS = [undefined, ...LIST_COLORS] as const;
const ICON_OPTIONS = [undefined, ...LIST_ICONS] as const;

/**
 * What a swatch or a tile is called out loud.
 *
 * The keys are storage, not language: read straight out, a screen reader
 * announces "fuchsia" and "sprout" in the same breath as the surrounding
 * sentence case and it is not obvious either is the name of a choice. This says
 * which of the two it is and leaves the key alone.
 */
const colorLabel = (color?: string) => (color ? `Colour: ${color}` : 'Colour: default');
const iconLabel = (icon?: string) => (icon ? `Icon: ${icon}` : 'Icon: default');

interface ListEditorProps {
  /**
   * The list being dressed, or null to make a new one. The only difference
   * between the two errands is what the fields start at and what the confirm
   * says, which is why they are one editor: a "new list" that cannot be given
   * the icon an existing one can is a gap the employer has to discover.
   */
  list: ConversationList | null;
  /**
   * The count line, passed in rather than worked out here: the panel already
   * says it for every other row, and two places counting the same members is
   * how the editing row comes to disagree with the one it replaced.
   */
  summary: string;
  onSave: (style: ListStyle) => void;
  onCancel: () => void;
}

/**
 * A list's row, with its parts made editable where they already are.
 *
 * Not a dialog and not a panel that unfolds underneath: the row keeps its
 * shape, and the two things an employer can change become the two things they
 * were already looking at — the chip opens the picker, the name takes a caret.
 * A colour is chosen to tell this list apart from the ones around it, so the
 * ones around it should stay on screen while it is chosen.
 *
 * This replaced a bare underlined input that saved on Enter and said so
 * nowhere, which was the actual complaint: an editor with no visible way to
 * commit looks broken. The tick is that way, and it stands exactly where the
 * pencil that opened this stood, so nothing moves under the cursor. Enter still
 * saves, and the hint in the field says so now rather than leaving it to be
 * found.
 */
export function ListEditor({ list, summary, onSave, onCancel }: ListEditorProps) {
  const [name, setName] = useState(list?.name ?? '');
  const [icon, setIcon] = useState<ListIcon | undefined>(list?.icon);
  const [color, setColor] = useState<ListColor | undefined>(list?.color);
  const [picking, setPicking] = useState(false);

  const pickerRef = useRef<HTMLDivElement>(null);
  useDismiss(picking, () => setPicking(false), pickerRef);

  /**
   * Bring the popover into view when it opens.
   *
   * It hangs below the chip inside a column that scrolls, so on a row near the
   * foot of a long list the icon grid opened past the panel's edge and was
   * simply cut off — with no way to reach the tiles but to scroll the row being
   * edited off the screen. `block: "nearest"` moves the column by the least it
   * can, so a picker that already fits does not move at all.
   */
  const openPicker = () => {
    setPicking((open) => !open);
    requestAnimationFrame(() => {
      pickerRef.current?.scrollIntoView({ block: 'nearest' });
    });
  };

  /**
   * Every pick hands the caret back to the name.
   *
   * Without it the press leaves focus on the swatch or the tile it landed on,
   * and Enter — which the hint two inches away promises will save — presses
   * that button again instead. So choosing an icon and hitting Enter did
   * nothing at all, twice.
   *
   * The picker deliberately stays open: colour and icon are two halves of one
   * errand, and closing after the first would make the second a second trip.
   */
  const nameRef = useRef<HTMLInputElement>(null);
  const refocus = () => nameRef.current?.focus();

  const trimmed = name.trim();
  const save = () => {
    if (trimmed) onSave({ name: trimmed, icon, color });
  };

  return (
    <div className={styles.row}>
      <div className={styles.badgeSlot} ref={pickerRef}>
        {/* The chip is the control, not a label beside one. It is already the
            thing that shows what the list looks like, so it is the thing to
            press to change it. */}
        <button
          type="button"
          className={styles.badgeButton}
          // Not "dialog": the popover traps nothing, takes no focus of its own
          // and closes on a click anywhere else, so announcing a dialog would
          // send a screen reader looking for one that was never opened.
          aria-haspopup="true"
          aria-expanded={picking}
          aria-label="Icon and colour"
          title="Icon and colour"
          onClick={openPicker}
        >
          <ListBadge list={{ icon, color }} />
          {/* The chip in this row is identical to the one in the row at rest,
              so on its own nothing says it can be pressed. This says it standing
              still: pinned over the disc's own corner, so the chip keeps its
              diameter and the layout never learns about it. */}
          <span className={styles.badgeHint}>
            <Icon name="chevronDown" size={10} />
          </span>
        </button>

        {picking && (
          <div className={styles.picker}>
            {/* Solid discs: this row is asked "which colour", and the answer
                reads fastest at full strength. The chip and the grid below are
                where the colour is worn, and they wear it the way a list
                actually does. */}
            <div className={styles.swatches} role="group" aria-label="Colour">
              {COLOR_OPTIONS.map((option) => (
                <button
                  key={option ?? 'default'}
                  type="button"
                  aria-label={colorLabel(option)}
                  aria-pressed={color === option}
                  className={`${styles.swatch} ${listTone(option)}`}
                  onClick={() => {
                    setColor(option);
                    refocus();
                  }}
                >
                  {color === option && <Icon name="check" size={13} />}
                </button>
              ))}
            </div>

            {/* The grid takes the chosen tone, so every glyph is already wearing
                the colour before it is picked — the two choices are one preview
                rather than two questions answered in the dark. */}
            <div className={`${styles.glyphs} ${listTone(color)}`} role="group" aria-label="Icon">
              {ICON_OPTIONS.map((option) => (
                <button
                  key={option ?? 'default'}
                  type="button"
                  aria-label={iconLabel(option)}
                  aria-pressed={icon === option}
                  className={icon === option ? styles.glyphOn : styles.glyph}
                  onClick={() => {
                    setIcon(option);
                    refocus();
                  }}
                >
                  <ListGlyph icon={option} size={17} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* The row's own middle column, kept whole: the name takes a caret, and
          the count below it stays exactly where it was. It is a fact about the
          list rather than something being edited, and taking it away shrank the
          row the moment editing began. */}
      <div className={styles.body}>
        <input
          ref={nameRef}
          className={styles.input}
          placeholder="List name"
          value={name}
          autoFocus
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') save();
            if (event.key === 'Escape') {
              // Stopped here so it cannot also reach anything listening on the
              // document: one press undoes one thing, never two.
              event.stopPropagation();
              // Innermost first. The caret is back in this field after every
              // pick (see `refocus`), so with the picker open this handler is
              // the one Escape reaches — and abandoning the whole edit there
              // threw away a half-typed name when all the employer meant was
              // to shut the popover.
              if (picking) setPicking(false);
              else onCancel();
            }
          }}
        />
        <span className={styles.summary}>{summary}</span>
      </div>

      {/* Where the pencil and the bin were, so the row's right-hand column does
          the same job in both states and nothing shifts on the way in. */}
      <span className={styles.actions}>
        {/* Against the tick, because it is what the tick does from the
            keyboard. And only once there is a name to save: advertising a key
            that would do nothing is worse than not mentioning it. */}
        {trimmed && <KbdKeys keys={['Enter']} />}
        <button
          type="button"
          className={styles.confirm}
          title={list ? 'Save' : 'Create'}
          disabled={!trimmed}
          onClick={save}
        >
          <Icon name="check" size={16} />
        </button>
        <button type="button" className={styles.iconBtn} title="Cancel" onClick={onCancel}>
          <Icon name="close" size={16} />
        </button>
      </span>
    </div>
  );
}
